import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ExternalLink, Download, AlertCircle, FileText, ZoomIn, ZoomOut } from 'lucide-react';


interface WordOnlineViewerProps {
  documentUrl: string;
  fileName?: string;
  className?: string;
  height?: string;
  onFileUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const loadingMessages = [
  "Preparing your document...",
  "Opening in Microsoft Office...",
  "Almost ready..."
];

export const WordOnlineViewer: React.FC<WordOnlineViewerProps> = ({
  documentUrl,
  fileName = 'Document',
  className = '',
  height = '600px',
  onFileUpload
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showOverlay, setShowOverlay] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const loadStartTime = useRef<number>(Date.now());
  const minimumDisplayTime = 1500; // 1.5 seconds minimum overlay display
  // Zoom and one or two pages side by side. The Office viewer is a sealed
  // frame, so zoom scales the frame, and the second page is a second viewer
  // opened at page 2.
  const [zoom, setZoom] = useState(1);
  const [pagesAcross, setPagesAcross] = useState<1 | 2>(1);
  const stepZoom = (d: number) => setZoom((z) => Math.min(2, Math.max(0.3, Math.round((z + d) * 10) / 10)));
  const areaRef = useRef<HTMLDivElement | null>(null);
  // Two pages: zoom so a whole page fits each half. One page: back to 100%.
  const showPages = (n: 1 | 2) => {
    setPagesAcross(n);
    const w = areaRef.current?.clientWidth ?? 0;
    if (n === 2 && w) setZoom(Math.min(1, Math.max(0.3, Math.floor((w / 2 / 880) * 20) / 20)));
    else setZoom(1);
  };

  // Load the viewer only once its tab is on screen. A frame loaded inside a
  // hidden tab has no size, and Office leaves it blank for good.
  const [onScreen, setOnScreen] = useState(false);
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const check = () => { if (el.clientWidth > 0) setOnScreen(true); };
    const ro = new ResizeObserver(check);
    ro.observe(el);
    check();
    return () => ro.disconnect();
  }, []);

  // Reset states when document URL changes
  useEffect(() => {
    setIsLoading(true);
    setShowOverlay(true);
    setHasError(false);
    setFadeOut(false);
    setLoadingMessageIndex(0);
    loadStartTime.current = Date.now();
    setRefreshKey(prev => prev + 1);
  }, [documentUrl]);

  // Cycle through loading messages
  useEffect(() => {
    if (!isLoading) return;
    
    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
    }, 1500);

    return () => clearInterval(interval);
  }, [isLoading]);

  // Build embed URL without excessive cache-busting (only use refreshKey)
  const embedUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(documentUrl)}&wdStartOn=1`;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openInNewTab = () => {
    window.open(embedUrl, '_blank');
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    
    // Calculate remaining time to show overlay (minimum display time)
    const elapsed = Date.now() - loadStartTime.current;
    const remainingTime = Math.max(0, minimumDisplayTime - elapsed);
    
    // Start fade out after minimum display time
    setTimeout(() => {
      setFadeOut(true);
      // Remove overlay after fade animation completes
      setTimeout(() => {
        setShowOverlay(false);
      }, 300); // Match CSS transition duration
    }, remainingTime);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setShowOverlay(false);
    setHasError(true);
    console.error('Failed to load document:', documentUrl);
  };

  return (
    <div className={`${className} h-full`}>
      <div className="h-full flex flex-col">
        {/* View controls */}
        <div className="flex items-center justify-end gap-1 border-b bg-background px-2 py-1 text-xs">
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => stepZoom(-0.1)} disabled={zoom <= 0.3} title="Zoom out" aria-label="Zoom out"><ZoomOut className="h-4 w-4" /></Button>
          <button type="button" className="w-12 text-center font-medium" onClick={() => setZoom(1)} title="Back to 100%">{Math.round(zoom * 100)}%</button>
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => stepZoom(0.1)} disabled={zoom >= 2} title="Zoom in" aria-label="Zoom in"><ZoomIn className="h-4 w-4" /></Button>
          <div className="mx-1 h-5 w-px bg-border" />
          <Button type="button" variant={pagesAcross === 1 ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2 text-xs" onClick={() => showPages(1)}>1 page</Button>
          <Button type="button" variant={pagesAcross === 2 ? 'secondary' : 'ghost'} size="sm" className="h-7 px-2 text-xs" onClick={() => showPages(2)}>2 pages</Button>
        </div>
        {/* Document viewer container */}
        <div ref={areaRef} className="relative w-full flex-1 min-h-0 bg-muted/30 rounded-lg overflow-hidden">
          
          {/* Branded Loading Overlay */}
          {showOverlay && (
            <div 
              className={`absolute inset-0 z-20 flex flex-col items-center justify-center bg-background transition-opacity duration-300 ${
                fadeOut ? 'opacity-0' : 'opacity-100'
              }`}
            >
              <div className="text-center space-y-6">
                {/* Loading message with smooth transition */}
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-5 h-5 text-primary animate-bounce" />
                    <span className="text-lg font-medium text-foreground">
                      {loadingMessages[loadingMessageIndex]}
                    </span>
                  </div>
                  
                  {/* Progress dots */}
                  <div className="flex justify-center gap-1.5">
                    {[0, 1, 2].map((i) => (
                      <div 
                        key={i}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          i <= loadingMessageIndex 
                            ? 'bg-primary scale-100' 
                            : 'bg-muted-foreground/30 scale-75'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Subtle file name display */}
                <p className="text-sm text-muted-foreground max-w-xs truncate px-4">
                  {fileName}
                </p>
              </div>
            </div>
          )}

          {/* Error state */}
          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-background z-10">
              <div className="text-center p-6 max-w-md">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
                <h3 className="font-semibold mb-2">Document Failed to Load</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  The document could not be displayed. This might be because:
                </p>
                <ul className="text-xs text-muted-foreground text-left mb-4 space-y-1">
                  <li>• The URL is not publicly accessible</li>
                  <li>• The file is not a valid .docx document</li>
                  <li>• Microsoft's viewer service is unavailable</li>
                </ul>
                <div className="flex gap-2 justify-center">
                  <Button size="sm" variant="outline" onClick={openInNewTab}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Try in New Tab
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleDownload}>
                    <Download className="w-4 h-4 mr-2" />
                    Download File
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Viewers - always rendered but behind overlay */}
          <div className="flex h-full w-full gap-1">
            {onScreen && Array.from({ length: pagesAcross }, (_, i) => (
              <div key={i} className="relative h-full min-w-0 flex-1 overflow-hidden">
                <iframe
                  key={`doc-${refreshKey}-${i}`}
                  src={embedUrl.replace('wdStartOn=1', `wdStartOn=${i + 1}`)}
                  frameBorder="0"
                  title={`Document Viewer - ${fileName}${i ? ' page 2' : ''}`}
                  className="border-0"
                  style={{
                    outline: 'none',
                    border: 'none',
                    visibility: showOverlay ? 'hidden' : 'visible',
                    width: `${100 / zoom}%`,
                    height: `${100 / zoom}%`,
                    transform: `scale(${zoom})`,
                    transformOrigin: 'top left',
                  }}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
                  onLoad={i === 0 ? handleIframeLoad : undefined}
                  onError={i === 0 ? handleIframeError : undefined}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
